import React, { useState, useEffect, useRef } from "react";
import { ErrorMessage, useFormikContext } from "formik";
import { EditIcon, QuestionMark } from "assets/svg";
import { UploaderIcon } from "assets/icons";
import { Avatar } from "antd";

const ProfileImageUploader = ({
  title,
  description,
  value,
  name,
  onChange,
}) => {
  const { errors, touched, setFieldValue, values } = useFormikContext();
  const [file, setFile] = useState(values[name] || null); // Initialize from Formik values
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isModelOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef(null); // Ref for the modal
  const mParentRef = useRef(null);
  const showError = touched[name] && errors[name];

  useEffect(() => {
    if (values[name] && values[name] !== file) {
      setFile(values[name]);
    }
  }, [values, name, file]);

  // Close modal on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsModalOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const handleFileInput = (file) => {
    const acceptType = getAcceptType();

    if (
      acceptType.includes(file.type) ||
      acceptType.includes(file.name.split(".").pop())
    ) {
      setError("");
      setFile(file);
      setFieldValue(name, file);
      onChange && onChange(file);
    } else {
      setError(
        `Invalid file format. Allowed formats: ${acceptType.join(", ")}`
      );
    }
  };

  const handleFileChange = (event) => {
    handleFileInput(event.target.files[0]);
  };

  const getAcceptType = () => {
    return ["image/jpeg", "image/png", "image/svg+xml"];
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileInput(e.dataTransfer.files[0]);
  };

  const handleInputClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    document.getElementById(`file-upload-${name}`).click();
  };

  return (
    <div className="w-full flex flex-col items-center md:flex-row gap-8 pt-4 pb-2">
      <div className="flex gap-8 items-start md:flex-1">
        {title && description && (
          <div className="flex flex-col">
            <p className="flex items-center gap-1 dark:text-white ">
              {title}{" "}
              {title && (
                <span className="text-[#94A3B8]">
                  <QuestionMark />
                </span>
              )}
            </p>
            <p className="flex text-[#737373] dark:text-gray-300">
              {description}
            </p>
          </div>
        )}

        {file ? (
          <div className="relative">
            <Avatar
              src={
                file instanceof File ? URL.createObjectURL(file) : value || ""
              }
              className="size-32"
            />
            <div
              ref={mParentRef}
              className="absolute top-[calc(100%-2.3rem)] z-50 left-[calc(100%-2.5rem)] flex flex-col gap-3"
            >
              <div
                className=" size-8 rounded-full bg-white overflow-hidden shadow-[0_0_5px_rgba(0,0,0,0.2)] cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsModalOpen(!isModelOpen);
                }}
              >
                <EditIcon className="text-secondary" />
              </div>
              {isModelOpen && (
                <div
                  ref={modalRef} // Reference to modal
                  className=" bg-white p-1 shadow-md rounded-md min-w-[100px] dark:bg-bgsecondary"
                >
                  <div
                    className="px-1.5 py-1 rounded-md hover:bg-gray-100 hover:dark:bg-bgprimary dark:text-white cursor-pointer"
                    onClick={(e) => {
                      handleInputClick(e);
                      setIsModalOpen(false);
                    }}
                  >
                    Upload New
                  </div>
                  <hr className="my-1 dark:border-gray-800" />
                  <div
                    className="px-1.5 py-1 text-red-500 rounded-md hover:bg-gray-100 hover:dark:bg-bgprimary cursor-pointer"
                    onClick={() => {
                      setFile(null);
                      setFieldValue(name, null);
                      setIsModalOpen(false);
                    }}
                  >
                    Delete
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div
              className={`size-32 border-2 border-dashed rounded-full cursor-pointer bg-white dark:bg-bgprimary overflow-hidden ${
                isDragging ? "bg-zinc-200 dark:bg-bgsecondary" : ""
              } ${
                showError
                  ? "border-red-500"
                  : "border-[#1996a6] dark:border-alpha"
              } `}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onDragLeave={handleDragLeave}
              onClick={handleInputClick}
            >
              <div className="flex flex-col justify-center items-center gap-2 h-full">
                <div className="[&>svg]:size-10">
                  <UploaderIcon />
                </div>
                <p className="text-[#737373] text-xs dark:text-gray-300 px-2 text-center">
                  {isDragging
                    ? "Drop image here"
                    : "Drag and drop your images here"}
                </p>
              </div>
            </div>
          </div>
        )}
        <input
          type="file"
          id={`file-upload-${name}`}
          accept="image/*"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
      {showError && (
        <ErrorMessage
          className="text-xs text-red-500 mt-1"
          name={name}
          component="div"
        />
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
};

export default ProfileImageUploader;
